// @ts-nocheck

import React, { memo, useContext } from 'react';
import { withTranslation } from 'react-i18next';

import InfoItem from './item/item';
import { getEventDateRange, getEventStartLocalDateTime } from '@/v2/helpers/moment/moment.utils.ts';
import {
  getShouldDisplayCompleteBreakdown,
  getShouldDisplaySingleFee,
  getShouldDisplayTaxes,
  getSingleFee,
} from '@/v2/helpers/fee/feeHelper.ts';

import styles from './info.module.scss';
import i18next from 'i18next';
import { getFormattedVenueAddress } from '../../models/eventModel';
import { getBestDescription, isTicket } from '../../models/ticketModel.ts';
import Charge from '../../wallet/info/item/charge.tsx';
import Charges from '../../wallet/info/item/charges.tsx';
import { getAdditionalInfos } from '@/v2/helpers/additionalInfo/additionalInfoHelper.tsx';
import { getEventLabels } from '@/v2/helpers/labels/labelsHelper.ts';
import InfoSection from '@/v2/wallet/info/section/section.tsx';
import { ChevronIcon, IconButton, TextStyle } from '@ticketmaster/global-design-system';
import { RootContext } from '@/root/root.provider.tsx';
import { EnvelopeFeeDisplayType, MultiEventEnvelopeModel } from '@/v2/models/multiEventEnvelopeModel.ts';
import { isProduct } from '@/v2/models/productModel.ts';

type Props = {
  envelope: MultiEventEnvelopeModel;
} & T;

function findActiveTicketAndEvent(envelope: MultiEventEnvelopeModel, activeTicketId: string) {
  for (let eventTickets of envelope.eventTickets) {
    for (let ticket of eventTickets.tickets) {
      if (ticket.id === activeTicketId) {
        return { activeEvent: eventTickets.event, activeItem: ticket };
      }
    }
  }
  return { activeEvent: null, activeItem: null };
}

function findActiveProductAndEvent(envelope: MultiEventEnvelopeModel, activeProductId: string) {
  for (let eventProducts of envelope.eventProducts) {
    for (let product of eventProducts.products) {
      if (product.id === activeProductId) {
        return { activeEvent: eventProducts.event, activeItem: product };
      }
    }
  }
  return { activeEvent: null, activeItem: null };
}

const Info = ({ t, envelope }: Props) => {
  const context = useContext(RootContext);
  const { activeTicketId, activeProductId } = context.state;
  const envelopeFeeDisplayType = EnvelopeFeeDisplayType[envelope.envelopeFeeDisplayType];
  let { activeEvent, activeItem } = activeTicketId
    ? findActiveTicketAndEvent(envelope, activeTicketId)
    : findActiveProductAndEvent(envelope, activeProductId);
  const currency = activeItem.currencyCode?.toUpperCase();
  const locale = i18next.language;
  const singleFee = getSingleFee(envelope, activeItem);
  const total = activeItem.price - activeItem.discount + singleFee + activeItem.tax;
  const subtotal =
    envelopeFeeDisplayType === EnvelopeFeeDisplayType.ROLLED_UP ? total : activeItem.price - activeItem.discount;

  const shouldDisplaySingleFee = getShouldDisplaySingleFee(envelopeFeeDisplayType, singleFee, activeItem.fees);
  const shouldDisplayCompleteBreakdown = getShouldDisplayCompleteBreakdown(envelopeFeeDisplayType, activeItem.fees);
  const shouldDisplayTaxes = getShouldDisplayTaxes(envelopeFeeDisplayType, activeItem);
  const shouldDisplayTotal = total !== subtotal;
  const shouldDisplayPrice = activeItem.price != null;
  const labels = getEventLabels(envelope, activeEvent?.id);

  const getBarcodeOrNoBarcodeMessage = (): string => {
    const { isSecureEntry, barcode } = activeItem;

    if (barcode) {
      return barcode;
    }

    return isSecureEntry ? t('WALLET.TICKETS.BARCODE_AVAILABLE_LATER') : t('WALLET.TICKETS.NO_BARCODE');
  };

  const getTermsAndConditions = (): string => {
    const { termsAndConditions } = activeItem;

    if (termsAndConditions) {
      return termsAndConditions;
    }
    return t('WALLET.INFO.TERMS_AND_CONDITIONS_ERROR');
  };

  return (
    <div className={styles.ticketInfoSidepanel}>
      <div className={styles.ticketInfoHeader}>
        <IconButton className={styles.ticketInfoHeaderIcon} onClick={() => context.setShowInfo(false)}>
          <ChevronIcon fillColor="#024DDF" rotate="90" />
        </IconButton>
        <TextStyle.Boising className={styles.ticketInfoHeaderTitle}>
          {t('WALLET.INFO.IMPORTANT_INFORMATION')}
        </TextStyle.Boising>
      </div>
      <div className={styles.ticketInfoDetails}>
        {isTicket(activeItem) && <InfoSection sectionName={'WALLET.INFO.TICKET_INFORMATION'}></InfoSection>}
        {isProduct(activeItem) && <InfoSection sectionName={'WALLET.INFO.PRODUCT_INFORMATION'}></InfoSection>}
        {!!envelope.locatorId && <InfoItem title={'WALLET.INFO.REFERENCE_ID'} data={envelope.locatorId} />}
        {!envelope.locatorId && !!envelope.orderId && (
          <InfoItem title={'WALLET.INFO.ORDER_ID'} titleOverride={labels?.orderNumber} data={envelope.orderId} />
        )}
        {isProduct(activeItem) && (
          <>
            <InfoItem title={'WALLET.INFO.PRODUCT_NAME'} data={activeItem.productName} />
            <InfoItem title={'WALLET.INFO.PRODUCT_VARIANT_NAME'} data={activeItem.productVariantName} />
            <InfoItem title={'WALLET.INFO.PRODUCT_VARIANT_SKU'} data={activeItem.productVariantSku} />
          </>
        )}
        <InfoItem
          title={'WALLET.INFO.EVENT_ID'}
          titleOverride={labels?.eventCode}
          data={activeEvent.systemId || activeEvent.discoveryId}
        />
        {!!activeEvent.venue.address && (
          <InfoItem
            title={'WALLET.INFO.VENUE_ADDRESS'}
            titleOverride={labels?.venueAddress}
            data={getFormattedVenueAddress(activeEvent.venue)}
          />
        )}
        {!!activeEvent.startLocalDate && (
          <InfoItem
            title={'WALLET.INFO.DATE_TIME'}
            data={getEventStartLocalDateTime(activeEvent.startLocalDate, activeEvent.startLocalTime)}
          />
        )}
        {!!activeEvent.dateRange && (
          <InfoItem title={'WALLET.INFO.DATE_TIME'} data={getEventDateRange(activeEvent.dateRange)} />
        )}
        {!activeEvent.startLocalDate && !activeEvent.dateRange && (
          <InfoItem title={'WALLET.INFO.DATE_TIME'} data={t('WALLET.INFO.TBA_SHORTEN')} />
        )}
        <InfoItem title={'WALLET.INFO.BARCODE'} data={getBarcodeOrNoBarcodeMessage()} />
        {isTicket(activeItem) && (
          <InfoItem
            title={'WALLET.INFO.TICKET_TYPE'}
            titleOverride={labels?.ticketType}
            data={getBestDescription(activeItem) || t('WALLET.TICKETS.DESCRIPTION')}
          />
        )}
        {shouldDisplayPrice && isTicket(activeItem) && (
          <InfoItem
            title={'WALLET.INFO.CHARGES'}
            data={[
              <Charge
                name={t('WALLET.INFO.CHARGES_TICKET_VALUE')}
                value={subtotal}
                locale={locale}
                currency={currency}
              />,
              shouldDisplaySingleFee && (
                <Charge
                  name={t('WALLET.INFO.CHARGES_TICKET_FEE')}
                  value={singleFee}
                  locale={locale}
                  currency={currency}
                />
              ),
              shouldDisplayCompleteBreakdown && (
                <Charges
                  name={t('WALLET.INFO.CHARGES_TICKET_FEE')}
                  charges={activeItem.fees}
                  locale={locale}
                  currency={currency}
                />
              ),
              shouldDisplayTaxes && (
                <Charge
                  name={t('WALLET.INFO.CHARGES_TICKET_TAX')}
                  value={activeItem.tax}
                  locale={locale}
                  currency={currency}
                />
              ),
              shouldDisplayTotal && (
                <Charge
                  name={t('WALLET.INFO.CHARGES_TICKET_TOTAL')}
                  value={total}
                  locale={locale}
                  currency={currency}
                />
              ),
            ]}
          />
        )}
        {shouldDisplayPrice && isProduct(activeItem) && (
          <InfoItem
            title={'WALLET.INFO.CHARGES'}
            data={[
              <Charge
                name={t('WALLET.INFO.CHARGES_PRODUCT_VALUE')}
                value={activeItem.price}
                locale={locale}
                currency={currency}
              />,
            ]}
          />
        )}
        {!!activeItem.additionalInfos && (
          <InfoItem title={'WALLET.INFO.ADDITIONAL_INFO'} data={getAdditionalInfos(activeItem, false)} />
        )}
        <hr className={styles.divider} />
        <InfoSection
          sectionName={'WALLET.INFO.TERMS_AND_CONDITIONS'}
          sectionNameOverride={labels?.termsAndConditions}
        ></InfoSection>
        <InfoItem data={getTermsAndConditions()} />
      </div>
    </div>
  );
};

export default withTranslation('translation')(memo(Info));
